import { apiService } from "../services/apiService";

const normalizeKey = (key: string) => key.replace(/[^a-z0-9]/gi, "").toLowerCase();

const pick = <T,>(
  obj: Record<string, unknown>,
  keys: string[],
): T | undefined => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key] as T;
    }
  }
  // El API alterna PascalCase, camelCase y guiones bajos entre endpoints.
  const buscados = new Set(keys.map(normalizeKey));
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null && buscados.has(normalizeKey(key))) {
      return value as T;
    }
  }
  return undefined;
};

export interface ManifoldEnvaseSerie {
  id: string;
  numeroSerie: string;
}

export interface ManifoldEnvasesResult {
  series: ManifoldEnvaseSerie[];
  horaInicio: string;
  horaTermino: string;
  /** Envases del expediente cuyo número de serie no pudo obtenerse. */
  sinNumeroSerie: number;
}

const CABECERA_KEYS = [
  "intCabecera",
  "IntCabecera",
  "id_Cabecera",
  "Id_Cabecera",
  "idCabecera",
];

/** La parada del expediente corresponde al Num_Ciclo de la estación. */
const PARADA_KEYS = ["numCiclo", "parada", "intParada"];

/** Identifica el expediente: un llenado concreto de una parada del manifold. */
export interface ExpedienteScope {
  intCabecera?: number;
  parada?: number;
  fechaLlenado?: string | null;
}

const formatTime = (value?: string | Date | null) => {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * El endpoint devuelve el histórico completo del manifold, pero el expediente
 * se emite por parada. Se acota con el criterio más preciso disponible:
 * cabecera, luego parada dentro del día de llenado y, por último, el día.
 */
const scopeDetalles = (
  items: Record<string, unknown>[],
  { intCabecera, parada, fechaLlenado }: ExpedienteScope,
): Record<string, unknown>[] => {
  const exponeCabecera = items.some(
    (item) => pick(item, CABECERA_KEYS) !== undefined,
  );

  if (exponeCabecera && intCabecera) {
    return items.filter(
      (item) => Number(pick(item, CABECERA_KEYS) ?? 0) === intCabecera,
    );
  }

  const dia = fechaLlenado ? new Date(fechaLlenado) : null;
  const diaValido = dia != null && !Number.isNaN(dia.getTime());

  const delDia = diaValido
    ? items.filter((item) => {
        const fecha = pick<string>(item, ["fechaRegistro", "FechaRegistro"]);
        if (!fecha) return false;
        const d = new Date(fecha);
        return !Number.isNaN(d.getTime()) && isSameDay(d, dia as Date);
      })
    : items;

  const exponeParada = items.some(
    (item) => pick(item, PARADA_KEYS) !== undefined,
  );

  if (exponeParada && parada) {
    return delDia.filter(
      (item) => Number(pick(item, PARADA_KEYS) ?? 0) === parada,
    );
  }

  return delDia;
};

export const loadManifoldEnvases = async (
  idManifold: number,
  scope: ExpedienteScope = {},
): Promise<ManifoldEnvasesResult> => {
  const { fechaLlenado } = scope;
  const detalles =
    (await apiService.get<unknown[]>(
      `/api/Catalogos/Envases_Detalle/ByManifold/${idManifold}`,
    )) ?? [];

  const items = detalles.map((raw) => (raw ?? {}) as Record<string, unknown>);
  const alcance = scopeDetalles(items, scope);

  const fechas: Date[] = [];
  const ids = alcance
    .map((item) => {
      const fecha = pick<string>(item, ["fechaRegistro", "FechaRegistro"]);
      if (fecha) {
        const d = new Date(fecha);
        if (!Number.isNaN(d.getTime())) fechas.push(d);
      }
      return String(pick(item, ["id_Envase", "Id_Envase", "idEnvase"]) ?? "");
    })
    .filter(Boolean);

  let horaInicio = "—";
  let horaTermino = "—";
  if (fechas.length > 0) {
    const min = new Date(Math.min(...fechas.map((d) => d.getTime())));
    const max = new Date(Math.max(...fechas.map((d) => d.getTime())));
    horaInicio = formatTime(min);
    horaTermino = formatTime(max);
  } else if (fechaLlenado) {
    const t = formatTime(fechaLlenado);
    horaInicio = t;
    horaTermino = t;
  }

  const uniqueIds = [...new Set(ids)];
  const resueltos = await Promise.all(
    uniqueIds.map(async (id) => {
      try {
        const envase = await apiService.getSecondary<Record<string, unknown>>(
          `/api/Envases/${id}`,
        );
        const numeroSerie = String(
          pick(envase ?? {}, ["numeroSerie", "NumeroSerie"]) ?? "",
        ).trim();
        return { id, numeroSerie };
      } catch {
        return { id, numeroSerie: "" };
      }
    }),
  );

  const series = resueltos.filter((s) => s.numeroSerie !== "");

  return {
    series,
    horaInicio,
    horaTermino,
    sinNumeroSerie: resueltos.length - series.length,
  };
};
